import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { caseStages } from '../data/caseStages';
import { CaseHeader } from '../components/cases/CaseHeader';
import { CaseProgress } from '../components/cases/CaseProgress';
import { CaseDetailsTabs } from '../components/cases/CaseDetailsTabs';
import { useCaseStore } from '../store/caseStore';
import { useTaskManagement } from '../hooks/useTaskManagement';
import { useDocumentManagement } from '../hooks/useDocumentManagement';
import { generateStageTasks } from '../data/taskTemplates';

export const CaseDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { cases, updateCase } = useCaseStore();
  const caseData = cases.find(c => c.id === id);

  const [viewingStageId, setViewingStageId] = useState<string | undefined>(
    caseData?.currentStageId
  );
  const [activeTab, setActiveTab] = useState(0);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // Set active tab from navigation state
  useEffect(() => {
    const state = location.state as { activeTab?: number };
    if (state?.activeTab !== undefined) {
      setActiveTab(state.activeTab);
    }
  }, [location.state]);

  if (!caseData) return <Typography>Case not found</Typography>;

  const currentStage = caseStages.find(stage => stage.id === (viewingStageId || caseData.currentStageId));
  if (!currentStage) return <Typography>Stage not found</Typography>;

  // Get tasks for the current view
  const currentTasks = useMemo(() => {
    const stageTasks = caseData.tasks.filter(t => t.stageId === currentStage.id);
    if (stageTasks.length === 0 && currentStage.id === caseData.currentStageId) {
      return generateStageTasks(currentStage.id);
    }
    return stageTasks;
  }, [currentStage.id, caseData.tasks, caseData.currentStageId]);

  const updateCaseData = (updates: Partial<typeof caseData>) => {
    updateCase(caseData.id, updates);
  };

  const addTimelineEvent = (event: Omit<(typeof caseData.timeline)[0], 'id'>) => {
    updateCaseData({
      timeline: [
        ...caseData.timeline,
        {
          ...event,
          id: `tl${caseData.timeline.length + 1}`,
        },
      ],
    });
  };

  const { handleTaskUpdate, handleTaskCreate } = useTaskManagement(
    caseData.tasks,
    (updatedTasks) => updateCaseData({ tasks: updatedTasks }),
    addTimelineEvent
  );

  const { handleDocumentUpload } = useDocumentManagement(
    caseData.documents,
    caseData.tasks,
    (updatedDocuments) => updateCaseData({ documents: updatedDocuments }),
    (updatedTasks) => updateCaseData({ tasks: updatedTasks }),
    addTimelineEvent
  );

  const handleCaseStatusChange = ({ status, currentStageId }: { status: typeof caseData.status; currentStageId: string }) => {
    // Get tasks for the new stage if it's different from the current stage
    const newStageTasks = currentStageId !== caseData.currentStageId
      ? generateStageTasks(currentStageId)
      : [];

    // Combine existing tasks with new stage tasks
    const updatedTasks = [
      ...caseData.tasks.filter(t => t.stageId !== currentStageId),
      ...newStageTasks,
    ];

    if (currentStageId !== caseData.currentStageId) {
      const newStage = caseStages.find(s => s.id === currentStageId);
      if (newStage) {
        addTimelineEvent({
          date: new Date().toISOString(),
          description: `Case moved to ${newStage.name} stage`,
          type: 'stage-change',
          addedBy: '1',
          stageId: currentStageId,
        });
      }
    }

    updateCaseData({
      status,
      currentStageId,
      tasks: updatedTasks,
    });
    setViewingStageId(currentStageId);
  };

  const handleFormOpen = (task: Task) => {
    setActiveTab(2);
    setSelectedTaskId(task.id);
  };

  const handleFormView = (taskId: string) => {
    setActiveTab(2);
    setSelectedTaskId(taskId);
  };

  const handleDocumentView = (documentId: string) => {
    setActiveTab(3);
  };

  const stagesByCategory = useMemo(() => {
    return caseStages.reduce((acc, stage) => {
      if (!acc[stage.category]) {
        acc[stage.category] = [];
      }
      acc[stage.category].push(stage);
      return acc;
    }, {} as Record<string, typeof caseStages>);
  }, []);

  const categories = Object.keys(stagesByCategory);
  const currentCategoryIndex = categories.indexOf(currentStage.category);
  const currentStages = stagesByCategory[currentStage.category];
  const currentStageIndex = currentStages.findIndex(s => s.id === currentStage.id);

  return (
    <Box>
      <CaseHeader case_={caseData} />
      
      <CaseProgress
        case_={caseData}
        currentStage={currentStage}
        currentStages={currentStages}
        currentStageIndex={currentStageIndex}
        currentCategoryIndex={currentCategoryIndex}
        categories={categories}
        onStatusChange={handleCaseStatusChange}
        onStageView={setViewingStageId}
      />

      <CaseDetailsTabs
        tasks={currentTasks}
        timeline={caseData.timeline}
        documents={caseData.documents}
        stageId={currentStage.id}
        onTaskUpdate={handleTaskUpdate}
        onTaskCreate={handleTaskCreate}
        activeTab={activeTab}
        onTabChange={(newTab) => setActiveTab(newTab)}
        onFormOpen={handleFormOpen}
        selectedTaskId={selectedTaskId}
        onTaskSelect={setSelectedTaskId}
        onAddTimelineNote={addTimelineEvent}
        onDocumentUpload={handleDocumentUpload}
        onFormView={handleFormView}
        onDocumentView={handleDocumentView}
      />
    </Box>
  );
};